import { zodResolver } from "@hookform/resolvers/zod"
import { Alert, AlertTitle, Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { AddOutlined, InfoOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { IMAGE_MIME_TYPES_WITH_PDF } from "@/components/upload/constants"
import type { CompanyCertification, CompanyCertificationType } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFileUploadWithValidation } from "@/hooks/useFileUploadWithValidation"
import { NotificationType } from "@/schemas/notification"
import CreateCertificationForm from "@/screens/Companies/edit/components/certification/components/forms/CreateCertificationForm"
import { BLANK_COMPANY_CERTIFICATION } from "@/screens/Companies/edit/components/certification/constants"
import useCompanyCertificationMutation from "@/screens/Companies/edit/components/certification/hooks/useCompanyCertificationMutation"
import type {
  CompanyCertificationAddFormValues,
  CompanyCertificationFormValues,
} from "@/screens/Companies/edit/components/certification/schema/CompanyCertificationSchema"
import { CompanyCertificationFormSchema } from "@/screens/Companies/edit/components/certification/schema/CompanyCertificationSchema"
import { hasExpiredCertificates } from "@/screens/Companies/edit/components/certification/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface AddCertificationDialogProps {
  certificationType?: CompanyCertificationType
  locations?: string[]
  companyId: string
  onClose: () => void
  onSuccess: () => void
}

const AddCertificationDialog: FC<AddCertificationDialogProps> = ({
  companyId,
  certificationType,
  locations,
  onClose,
  onSuccess,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { createCertification } = useCompanyCertificationMutation()
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)
  const [isUpcomingEnable, setIsUpcomingEnable] = useState<boolean>(false)

  const { uploadFile } = useFileUploadWithValidation({
    referenceTableName: ReferenceTableName.Company,
    allowedFileTypes: IMAGE_MIME_TYPES_WITH_PDF,
  })

  const methods = useForm<CompanyCertificationFormValues>({
    defaultValues: {
      certificationType: certificationType ?? "",
      locationIds: locations ?? [],
      newCompanyCertification: [BLANK_COMPANY_CERTIFICATION],
    },
    mode: "all",
    resolver: zodResolver(CompanyCertificationFormSchema),
  })

  const {
    fields: newCompanyCertificationFields,
    append,
    remove,
  } = useFieldArray({ control: methods.control, name: "newCompanyCertification" })

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid },
  } = methods

  const newCompanyCertifications = watch("newCompanyCertification")

  const shouldDisplayExpirationWarning = hasExpiredCertificates(newCompanyCertifications)

  const handleCancel = () => {
    setIsCancelConfirmationOpen(false)
    onClose()
  }

  const handleConfirm = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleRemoveUpcomingCertification = (index: number) => {
    remove(index)
    setIsUpcomingEnable(false)
  }

  const createSingleCertification = async (
    certData: CompanyCertificationAddFormValues,
    formData: Omit<CompanyCertificationFormValues, "newCompanyCertification">
  ): Promise<boolean> => {
    const { error, data } = await createCertification({
      input: {
        ...formData,
        companyId,
        certificationType: formData.certificationType as CompanyCertificationType,
        effectiveDate: certData.effectiveDate,
        expirationDate: certData.expirationDate,
      },
    })
    const createdData = data?.companyCertification?.create
    const isFailure = error?.message || createdData?.__typename === "CompanyCertificationFailure"

    if (isFailure) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to create certification",
      })
      return false
    }

    const image = certData.image as File

    if (image && createdData?.__typename === "CompanyCertificationCreateSuccess") {
      const certifications = createdData.companyCertification as CompanyCertification[]
      return await uploadImages(image, certifications)
    }

    return true
  }

  const uploadImages = async (image: File, certifications: CompanyCertification[]): Promise<boolean> => {
    try {
      const uploadPromises = certifications.map(async (cert) => {
        const success = await uploadFile(image, FieldGroupEnum.CompanyCertification, cert.companyCertificationId!)
        return success ? { success: true } : null
      })

      const results = await Promise.all(uploadPromises)
      const successfulUploads = results.filter((res) => res !== null)

      if (!successfulUploads.length) {
        throw new Error(`Unable to upload image: ${image.name}. Please try again.`)
      }

      return true
    } catch (err) {
      notify({
        type: NotificationType.Error,
        message: err instanceof Error ? err.message : "Image upload failed. Please try again.",
      })
      return false
    }
  }

  const handleSubmitData = async ({ newCompanyCertification, ...formData }: CompanyCertificationFormValues) => {
    const results = await Promise.all(newCompanyCertification.map((cert) => createSingleCertification(cert, formData)))

    const allSuccessful = results.every((result) => result)

    if (allSuccessful) {
      notify({
        type: NotificationType.Success,
        silent: false,
        content: "Certification successfully added.",
      })
      onSuccess()
    }
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog
          title='Are you sure you want to cancel?'
          message='By cancelling all of the information inputed will be lost. Confirm by clicking on “Yes, Cancel” or “Go Back” to keep adding information.'
          onClose={() => setIsCancelConfirmationOpen(false)}
          onConfirm={handleCancel}
        />
      )}
      <Dialog open onClose={handleConfirm} fullWidth sx={{ display: isCancelConfirmationOpen ? "none" : "block" }}>
        <DialogHeader title='Add Certification' onClose={handleConfirm} />
        <DialogContent classes={{ root: "p-10" }}>
          {shouldDisplayExpirationWarning && (
            <Alert
              icon={<InfoOutlined color='primary' />}
              severity='info'
              color='info'
              variant='filled'
              className='mb-6'
              classes={{
                message: "w-full flex flex-row justify-between",
              }}
            >
              <Box>
                <AlertTitle color='primary'>The expiration date you selected is in the past.</AlertTitle>
                <Typography variant='body2' color='primary'>
                  This certificate will be added to the “Archived” historical data if an active certificate already
                  exists.
                </Typography>
              </Box>
            </Alert>
          )}

          <FormProvider {...methods}>
            <CreateCertificationForm
              companyId={companyId}
              isUpcomingEnable={isUpcomingEnable}
              fields={newCompanyCertificationFields}
              onCertificationRemove={handleRemoveUpcomingCertification}
            />
          </FormProvider>
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button appearance='outlined' onClick={handleConfirm} data-testid='add-certification-cancel-button'>
            Cancel
          </Button>
          <Box className='flex gap-2'>
            {!isUpcomingEnable && (
              <Button
                data-testid='add-certification-additional-certificate-button'
                appearance='text'
                onClick={() => {
                  append(BLANK_COMPANY_CERTIFICATION)
                  setIsUpcomingEnable(true)
                }}
              >
                <AddOutlined />
                Additional Certificate
              </Button>
            )}

            <Button
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit(handleSubmitData)}
              data-testid='add-certification-save-button'
            >
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AddCertificationDialog
